import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { Loader } from "@culturehq/components";
import VideoThumbnails from "components/stories/video-studio/timeline/elements/VideoThumbnails";
import { CreatomateContext } from "./stories/video-studio/contexts/VideoStudioContext";
import { ThumbnailProvider } from "./stories/video-studio/timeline/ThumbnailsContext";

const PlaybackGrabberFrame = ({
  cursorMarkerRef,
  onVideoRefUpdateTime,
  preview,
  videoUrl,
  videoRef,
  videoDuration
}) => {
  const playbackBarRef = useRef();
  const seekableBarRef = useRef();
  const [playbackRect, setPlaybackRect] = useState(null);
  const [containerWidth, setContainerWidth] = useState();
  const { getActiveElement } = useContext(CreatomateContext);
  const activeElement = getActiveElement(preview);

  const element = useMemo(() => {
    if (activeElement) return activeElement;
    const firstVideoElement = preview.state?.elements?.find(
      element => element.source.type === "video"
    );
    if (firstVideoElement) return firstVideoElement;

    return {
      duration: videoDuration,
      source: {
        source: videoUrl
      }
    };
  },
  [activeElement, preview.state.elements, videoDuration, videoUrl]);

  const playbackRectRef = useRef();
  playbackRectRef.current = playbackRect;

  useEffect(() => {
    const updateContainerWidth = pixelRatio => () => {
      if (playbackBarRef.current) {
        const rect = playbackBarRef.current.getBoundingClientRect();
        setContainerWidth(rect.width * pixelRatio);
      }
    };
    window.addEventListener("resize", updateContainerWidth(1));
    updateContainerWidth(2)();

    return () => {
      window.removeEventListener("resize", updateContainerWidth(1));
    };
  }, []);

  const pauseVideo = () => {
    videoRef.current.pause();
  };

  const handleSeekMouseDown = event => {
    pauseVideo();
    const rect = playbackBarRef.current.getBoundingClientRect();
    const seekAmount = (event.clientX - rect.left) / rect.width;
    setPlaybackRect(rect);
    seekVideo(seekAmount);
    window.addEventListener("mousemove", playheadMove);
    window.addEventListener("mouseup", playheadDrop);
  };

  const playheadMove = event => {
    const seekAmount = (event.clientX
      - playbackRectRef.current.left) / playbackRectRef.current.width;
    seekVideo(seekAmount);
  };

  const playheadDrop = () => {
    window.removeEventListener("mousemove", playheadMove);
    window.removeEventListener("mouseup", playheadDrop);
  };

  const handleSeekableBarClick = event => {
    pauseVideo();
    const rect = playbackBarRef.current.getBoundingClientRect();
    const seekAmount = (event.clientX
      - playbackRectRef.current.left) / playbackRectRef.current.width;
    setPlaybackRect(rect);
    seekVideo(seekAmount);
    window.removeEventListener("mousemove", playheadMove);
  };

  const seekVideo = seekRatio => {
    if (videoRef.current.duration * seekRatio !== Infinity) {
      onVideoRefUpdateTime(videoRef.current.duration * seekRatio);
    }
  };

  if (!Number.isFinite(videoDuration)) {
    return <Loader loading />;
  }

  return (
    <div className="playback" ref={playbackBarRef}>
      {containerWidth && (
        <VideoThumbnails
          element={element}
          containerWidth={containerWidth}
          containerHeight={80}
          startTime={0}
          endTime={videoDuration}
          thumbnailsToShowCountFixed={7}
        />
      )}
      <div className="seekable" ref={seekableBarRef} onMouseDown={handleSeekMouseDown} onClick={handleSeekableBarClick} />
      <div className="cursor-marker" ref={cursorMarkerRef} onMouseDown={handleSeekMouseDown} />
    </div>
  );
};


export default props => {
  const { preview } = props;
  return (
    <ThumbnailProvider preview={preview}>
      <PlaybackGrabberFrame {...props} />
    </ThumbnailProvider>
  );
};
